// Escolher alimento por rolagem: primeiro os que a pessoa mais usa, depois a lista pronta da refeição.
// Depois de escolher, informa a quantidade (ou as gramas) e pode editar o nome (ex.: "Suco" vira "Suco de uva").
import { useMemo, useState } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import type { Item, Unidade } from '../../store/types';
import { useDoc } from '../../store/store';
import { maisUsados } from '../../store/bemestar';
import { ALIMENTOS, UNIDADES, type TipoRefeicao } from '../../lib/bemestarCatalogo';
import { Btn, Chip, Field, Sheet } from '../ui';
import { C, alpha } from '../theme';
import { norm, uid } from '../../lib/date';
import { t, tn } from '../../lib/i18n';
import { ROLAGEM_TECLADO } from '../teclado';

const NOME_TIPO: Record<TipoRefeicao, string> = { cafe: 'Café da manhã', almoco: 'Almoço', lanche: 'Lanche', jantar: 'Jantar' };

/** "2 ovos", "150 g Arroz": texto curto da quantidade. */
export function qtdTxt(i: Pick<Item, 'qtd' | 'un'>) {
  if (!i.qtd) return '';
  const n = String(i.qtd).replace('.', ',');
  if (!i.un || i.un === 'un') return `${n}×`;
  if (i.un === 'g' || i.un === 'ml') return `${n} ${i.un}`;
  return `${n} ${tn(UNIDADES.find(u => u.id === i.un)?.nome ?? i.un, Number(i.qtd))}`;
}

export function EscolherAlimento({ visible, tipo, cor, inicial, onClose, onPronto }: {
  visible: boolean; tipo: TipoRefeicao; cor: string; inicial?: Item | null; onClose: () => void; onPronto: (i: Item) => void;
}) {
  const d = useDoc('bemestar');
  const [busca, setBusca] = useState('');
  const [sel, setSel] = useState<{ nome: string; qtd: string; un: Unidade } | null>(null);
  const [aberto, setAberto] = useState(false);
  // abre já na quantidade quando é para editar um item
  if (visible !== aberto) {
    setAberto(visible);
    if (visible) { setBusca(''); setSel(inicial ? { nome: inicial.text, qtd: inicial.qtd ? String(inicial.qtd).replace('.', ',') : '', un: inicial.un ?? 'un' } : null); }
  }
  const usados = useMemo(() => maisUsados(d).slice(0, 12), [d]);
  const prontos = ALIMENTOS[tipo];
  const q = norm(busca.trim());
  const filtra = <X extends { nome: string }>(l: X[]) => (q ? l.filter(x => norm(t(x.nome)).includes(q)) : l);
  const outros = q ? (Object.keys(ALIMENTOS) as TipoRefeicao[]).filter(k => k !== tipo).flatMap(k => ALIMENTOS[k]).filter(x => norm(t(x.nome)).includes(q))
    .filter((x, i, l) => l.findIndex(y => y.nome === x.nome) === i && !prontos.some(p => p.nome === x.nome)) : [];

  const linha = (nome: string, icon: string, sub: string, onPress: () => void, k: string) => (
    <Pressable key={k} onPress={onPress} style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, borderRadius: 12, backgroundColor: pressed ? C.card2 : C.card })}>
      <Text style={{ fontSize: 22, width: 30, textAlign: 'center' }}>{icon}</Text>
      <Text style={{ color: C.text, flex: 1 }}>{nome}</Text>
      {!!sub && <Text style={{ color: C.sub, fontSize: 12 }}>{sub}</Text>}
    </Pressable>
  );
  const num = (s: string) => { const x = Number(s.replace(',', '.')); return isFinite(x) && x > 0 ? x : undefined; };
  const passo = sel && (sel.un === 'g' || sel.un === 'ml') ? 10 : 1;
  const mudar = (dv: number) => sel && setSel({ ...sel, qtd: String(Math.max(0, Math.round(((num(sel.qtd) ?? 0) + dv) * 10) / 10)).replace('.', ',') });

  return (
    <Sheet visible={visible} onClose={onClose} title={sel ? t('Quantidade') : t('Escolher alimento')}>
      {!sel ? (<>
        <View style={{ paddingHorizontal: 16, paddingBottom: 8 }}>
          <Field value={busca} onChangeText={setBusca} placeholder={t('Buscar ou digitar um alimento')} autoCorrect={false} />
        </View>
        <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 60, gap: 6 }}>
          {!!busca.trim() && linha(t('Usar "{nome}"', { nome: busca.trim() }), '✏️', '', () => setSel({ nome: busca.trim(), qtd: '1', un: 'un' }), 'novo')}
          {!!filtra(usados).length && <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 6 }}>{t('MAIS USADOS')}</Text>}
          {filtra(usados).map(u => linha(u.nome, '⭐', u.qtd ? qtdTxt(u) : '', () => setSel({ nome: u.nome, qtd: u.qtd ? String(u.qtd).replace('.', ',') : '1', un: u.un ?? 'un' }), 'u' + u.nome))}
          <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 6 }}>{t('PARA {refeicao}', { refeicao: t(NOME_TIPO[tipo]).toUpperCase() })}</Text>
          {filtra(prontos).map(a => linha(t(a.nome), a.icon, '', () => setSel({ nome: t(a.nome), qtd: String(a.qtd).replace('.', ','), un: a.un }), 'p' + a.nome))}
          {outros.map(a => linha(t(a.nome), a.icon, '', () => setSel({ nome: t(a.nome), qtd: String(a.qtd).replace('.', ','), un: a.un }), 'o' + a.nome))}
        </ScrollView>
      </>) : (
        <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }}>
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Nome (pode editar, ex.: "Suco de uva")')}</Text>
          <Field value={sel.nome} onChangeText={nome => setSel({ ...sel, nome })} />
          <Text style={{ color: C.sub, fontSize: 12 }}>{t('Quantidade')}</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <Pressable onPress={() => mudar(-passo)} style={{ width: 46, height: 46, borderRadius: 23, backgroundColor: C.card2, alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ color: C.text, fontSize: 24 }}>−</Text></Pressable>
            <Field value={sel.qtd} onChangeText={qtd => setSel({ ...sel, qtd })} keyboardType="decimal-pad" style={{ flex: 1, textAlign: 'center', fontSize: 22, fontWeight: '800' }} />
            <Pressable onPress={() => mudar(passo)} style={{ width: 46, height: 46, borderRadius: 23, backgroundColor: cor, alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ color: '#fff', fontSize: 24 }}>+</Text></Pressable>
          </View>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {UNIDADES.map(u => <Chip key={u.id} small cor={cor} on={sel.un === u.id} label={tn(u.nome, 2)} onPress={() => setSel({ ...sel, un: u.id })} />)}
          </View>
          <View style={{ backgroundColor: alpha(cor, 0.12), borderRadius: 12, padding: 12 }}>
            <Text style={{ color: C.text, fontWeight: '700' }}>{[qtdTxt({ qtd: num(sel.qtd), un: sel.un }), sel.nome.trim()].filter(Boolean).join(' ')}</Text>
          </View>
          <Btn cor={cor} disabled={!sel.nome.trim()} label={inicial ? t('Salvar') : t('Adicionar')}
            onPress={() => { onPronto({ id: inicial?.id ?? uid(), text: sel.nome.trim(), done: false, qtd: num(sel.qtd), un: sel.un }); onClose(); }} />
          {!inicial && <Btn outline cor={cor} label={'‹ ' + t('Escolher outro')} onPress={() => setSel(null)} />}
        </ScrollView>
      )}
    </Sheet>
  );
}
